import { useEffect, useState, useRef, useCallback } from 'react';

/**
 * Custom hook for WebSocket connection with auto-reconnect
 * Provides real-time notification delivery when FCM is not available
 *
 * @param {string} url - WebSocket server URL
 * @param {string} token - JWT authentication token
 * @param {boolean} enabled - Whether to enable WebSocket connection
 * @returns {Object} - Connection state and notifications
 */
export const useWebSocket = (url, token, enabled = true) => {
    const [isConnected, setIsConnected] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [connectionError, setConnectionError] = useState(null);

    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const heartbeatIntervalRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);

    const MAX_RECONNECT_ATTEMPTS = 5;
    const RECONNECT_DELAY = 5000; // 5 seconds
    const HEARTBEAT_INTERVAL = 30000; // 30 seconds

    /**
     * Clean up WebSocket connection and timers
     */
    const cleanup = useCallback(() => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (wsRef.current) {
            wsRef.current.onopen = null;
            wsRef.current.onmessage = null;
            wsRef.current.onerror = null;
            wsRef.current.onclose = null;

            if (wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.close();
            }

            wsRef.current = null;
        }
    }, []);

    /**
     * Start heartbeat ping/pong to keep connection alive
     */
    const startHeartbeat = useCallback(() => {
        heartbeatIntervalRef.current = setInterval(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(
                    JSON.stringify({
                        type: 'ping',
                        timestamp: Date.now(),
                    })
                );
            }
        }, HEARTBEAT_INTERVAL);
    }, []);

    /**
     * Connect to WebSocket server
     */
    const connect = useCallback(() => {
        if (!enabled || !token || !url) {
            return;
        }

        try {
            const ws = new WebSocket(url);
            wsRef.current = ws;

            ws.onopen = () => {
                setIsConnected(true);
                setConnectionError(null);
                reconnectAttemptsRef.current = 0;

                // Send authentication message
                ws.send(
                    JSON.stringify({
                        type: 'auth',
                        token: token,
                    })
                );

                // Start heartbeat
                startHeartbeat();
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    // Handle authentication success
                    if (data.type === 'auth_success') {
                        console.log('✅ WebSocket authenticated for user:', data.uid);
                    }

                    // Handle authentication error
                    if (data.type === 'auth_error') {
                        console.error('❌ WebSocket authentication failed:', data.message);
                        setConnectionError(data.message);
                        cleanup();
                    }

                    // Handle incoming notification
                    if (data.type === 'notification') {
                        setNotifications((prev) => [data.data, ...prev]);
                    }

                    // Handle pong response
                    if (data.type === 'pong') {
                        // Heartbeat acknowledged
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            ws.onerror = (error) => {
                console.error('❌ WebSocket error:', error);
                setConnectionError('Connection error');
            };

            ws.onclose = (event) => {
                setIsConnected(false);

                // Clear heartbeat
                if (heartbeatIntervalRef.current) {
                    clearInterval(heartbeatIntervalRef.current);
                    heartbeatIntervalRef.current = null;
                }

                // Keep reconnecting (2026-11-04: it used to give up after 5 tries, leaving the bell
                // without live updates until a reload) — 5 s, 10 s, 20 s … capped at 60 s.
                if (enabled) {
                    const wait = Math.min(60000, RECONNECT_DELAY * 2 ** Math.min(reconnectAttemptsRef.current, 4));
                    reconnectAttemptsRef.current += 1;
                    if (reconnectAttemptsRef.current === MAX_RECONNECT_ATTEMPTS) setConnectionError('Reconnecting…');
                    reconnectTimeoutRef.current = setTimeout(() => {
                        connect();
                    }, wait);
                }
            };
        } catch (error) {
            console.error('Failed to create WebSocket connection:', error);
            setConnectionError(error.message);
        }
    }, [url, token, enabled, startHeartbeat, cleanup]);

    /**
     * Send message through WebSocket
     */
    const sendMessage = useCallback((data) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(data));
            return true;
        }
        return false;
    }, []);

    /**
     * Clear all notifications
     */
    const clearNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    /**
     * Initialize WebSocket connection
     */
    useEffect(() => {
        if (enabled && token && url) {
            connect();
        }

        return () => {
            cleanup();
        };
    }, [url, token, enabled, connect, cleanup]);

    return {
        isConnected,
        notifications,
        connectionError,
        sendMessage,
        clearNotifications,
        reconnect: connect,
    };
};

export default useWebSocket;
